import React, { useState, useEffect, useReducer, useContext, useCallback } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import AddIcon from "@material-ui/icons/Add";
import PeopleIcon from "@material-ui/icons/People";
import DescriptionIcon from "@material-ui/icons/Description";
import AssessmentIcon from "@material-ui/icons/Assessment";
import Badge from "@material-ui/core/Badge";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import { isArray } from "lodash";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";
import { AuthContext } from "../../context/Auth/AuthContext";
import MaturationModal from "../../components/MaturationModal";

const reducer = (state, action) => {
  if (action.type === "LOAD_DATA") {
    const data = action.payload;
    const newData = [];
    if (isArray(data)) {
      data.forEach((item) => {
        const index = state.findIndex((u) => u.id === item.id);
        if (index !== -1) {
          state[index] = item;
        } else {
          newData.push(item);
        }
      });
    }
    return [...state, ...newData];
  }
  if (action.type === "UPDATE_DATA") {
    const item = action.payload;
    const index = state.findIndex((u) => u.id === item.id);
    if (index !== -1) {
      state[index] = item;
      return [...state];
    } else {
      return [item, ...state];
    }
  }
  if (action.type === "DELETE_DATA") {
    const id = action.payload;
    const index = state.findIndex((u) => u.id === id);
    if (index !== -1) {
      state.splice(index, 1);
    }
    return [...state];
  }
  if (action.type === "RESET") {
    return [];
  }
  return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  tabsContainer: {
    marginBottom: theme.spacing(2),
  },
  statsCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
  },
  tableHeader: {
    fontWeight: "bold",
    backgroundColor: theme.palette.grey[100],
  },
  statusChip: {
    fontSize: "0.75rem",
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
}));

const Maturations = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { datetimeToClient } = useDate();

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Maturações
  const [maturations, dispatchMaturations] = useReducer(reducer, []);
  const [selectedMaturation, setSelectedMaturation] = useState(null);
  const [deletingMaturation, setDeletingMaturation] = useState(null);
  const [maturationModalOpen, setMaturationModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  // Contatos do Pool
  const [contacts, dispatchContacts] = useReducer(reducer, []);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactForm, setContactForm] = useState({ name: "", number: "", carrier: "", notes: "" });
  const [deletingContact, setDeletingContact] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [confirmContactModalOpen, setConfirmContactModalOpen] = useState(false);

  // Verificar permissão
  useEffect(() => {
    if (!user) return;
    
    // Verificar se o plano permite maturação
    const checkPermission = async () => {
      try {
        const { getPlanCompany } = await import("../../hooks/usePlans");
        const planConfigs = await getPlanCompany(undefined, user.companyId);
        if (!planConfigs?.plan?.useMaturation) {
          toast.error("Seu plano não possui acesso ao módulo de Maturação!");
          setTimeout(() => history.push("/"), 1000);
          return;
        }
      } catch (err) {
        console.error("[Maturations] Erro ao verificar permissão:", err);
        // Não bloquear o acesso se houver erro na verificação
        // toastError(err);
      }
    };
    
    checkPermission();
  }, [user, history]);

  // Reset ao mudar filtros
  useEffect(() => {
    if (activeTab === 0) {
      dispatchMaturations({ type: "RESET" });
    } else {
      dispatchContacts({ type: "RESET" });
    }
    setPageNumber(1);
  }, [searchParam, statusFilter, pageSize, activeTab]);

  const fetchMaturations = useCallback(async () => {
    if (!user?.companyId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data } = await api.get("/maturations", {
        params: {
          searchParam,
          pageNumber,
          pageSize,
          status: statusFilter,
        },
      });

      if (pageNumber === 1) {
        dispatchMaturations({ type: "RESET" });
      }
      dispatchMaturations({ type: "LOAD_DATA", payload: data?.records || [] });
      setTotalPages(data?.totalPages || 1);
      setTotalCount(data?.count || 0);
      setLoading(false);
    } catch (err) {
      console.error("[Maturations] Erro ao buscar maturações:", err);
      // Não mostrar toast de erro se for 404 (tabela não existe ainda)
      if (err?.response?.status !== 404) {
        toastError(err);
      }
      setLoading(false);
      dispatchMaturations({ type: "RESET" });
    }
  }, [searchParam, pageNumber, pageSize, statusFilter, user]);

  // Função para buscar contatos do pool
  const fetchContacts = useCallback(async () => {
    if (!user?.companyId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data } = await api.get("/maturation-contact-pools", {
        params: {
          searchParam,
          pageNumber,
          pageSize,
        },
      });

      if (pageNumber === 1) {
        dispatchContacts({ type: "RESET" });
      }
      dispatchContacts({ type: "LOAD_DATA", payload: data?.records || [] });
      setTotalPages(data?.totalPages || 1);
      setTotalCount(data?.count || 0);
      setLoading(false);
    } catch (err) {
      console.error("[Maturations] Erro ao buscar contatos:", err);
      // Não mostrar toast de erro se for 404 (tabela não existe ainda)
      if (err?.response?.status !== 404) {
        toastError(err);
      }
      setLoading(false);
      dispatchContacts({ type: "RESET" });
    }
  }, [searchParam, pageNumber, pageSize, user]);

  // Carregar dados
  useEffect(() => {
    if (!user) return;
    
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      if (activeTab === 0) {
        fetchMaturations();
      } else {
        fetchContacts();
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, statusFilter, pageSize, activeTab, fetchMaturations, fetchContacts, user]);

  // Socket listeners
  useEffect(() => {
    if (!user || !user.companyId || !socket) return;

    const companyId = user.companyId;

    const onMaturation = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatchMaturations({ type: "UPDATE_DATA", payload: data.record });
      }
      if (data.action === "delete") {
        dispatchMaturations({ type: "DELETE_DATA", payload: +data.record.id });
      }
    };

    const onMaturationContactPool = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatchContacts({ type: "UPDATE_DATA", payload: data.record });
      }
      if (data.action === "delete") {
        dispatchContacts({ type: "DELETE_DATA", payload: +data.id });
      }
    };

    socket.on(`company-${companyId}-Maturation`, onMaturation);
    socket.on(`company-${companyId}-MaturationContactPool`, onMaturationContactPool);

    return () => {
      socket.off(`company-${companyId}-Maturation`, onMaturation);
      socket.off(`company-${companyId}-MaturationContactPool`, onMaturationContactPool);
    };
  }, [user, socket]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handlePageChange = (event, newPage) => {
    setPageNumber(newPage + 1);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPageNumber(1);
  };

  const handleOpenModal = () => {
    setSelectedMaturation(null);
    setMaturationModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedMaturation(null);
    setMaturationModalOpen(false);
  };

  const handleEdit = (maturation) => {
    setSelectedMaturation(maturation);
    setMaturationModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/maturations/${deletingMaturation.id}`);
      toast.success("Maturação excluída com sucesso!");
      dispatchMaturations({ type: "DELETE_DATA", payload: deletingMaturation.id });
      setConfirmModalOpen(false);
      setDeletingMaturation(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleStart = async (maturation) => {
    try {
      const { data } = await api.post(`/maturations/${maturation.id}/start`);
      toast.success("Maturação iniciada com sucesso!");
      // Atualizar imediatamente com os dados retornados
      dispatchMaturations({ type: "UPDATE_DATA", payload: data });
      // Também recarregar após um pequeno delay para garantir sincronização
      setTimeout(() => {
        fetchMaturations();
      }, 500);
    } catch (err) {
      toastError(err);
    }
  };

  const handlePause = async (maturation) => {
    try {
      const { data } = await api.post(`/maturations/${maturation.id}/pause`);
      toast.success("Maturação pausada com sucesso!");
      // Atualizar imediatamente com os dados retornados
      dispatchMaturations({ type: "UPDATE_DATA", payload: data });
      // Também recarregar após um pequeno delay para garantir sincronização
      setTimeout(() => {
        fetchMaturations();
      }, 500);
    } catch (err) {
      toastError(err);
    }
  };

  const handleResume = async (maturation) => {
    try {
      const { data } = await api.post(`/maturations/${maturation.id}/resume`);
      toast.success("Maturação retomada com sucesso!");
      // Atualizar imediatamente com os dados retornados
      dispatchMaturations({ type: "UPDATE_DATA", payload: data });
      // Também recarregar após um pequeno delay para garantir sincronização
      setTimeout(() => {
        fetchMaturations();
      }, 500);
    } catch (err) {
      toastError(err);
    }
  };

  const formatStatus = (status) => {
    const statusMap = {
      INATIVA: "Inativa",
      PROGRAMADA: "Programada",
      EM_ANDAMENTO: "Em Andamento",
      PAUSADA: "Pausada",
      CANCELADA: "Cancelada",
      FINALIZADA: "Finalizada",
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      INATIVA: "default",
      PROGRAMADA: "primary",
      EM_ANDAMENTO: "primary",
      PAUSADA: "secondary",
      CANCELADA: "default",
      FINALIZADA: "default",
    };
    return colorMap[status] || "default";
  };

  // Handlers para contatos
  const handleOpenContactModal = () => {
    setSelectedContact(null);
    setContactForm({ name: "", number: "", carrier: "", notes: "" });
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContact(null);
    setContactForm({ name: "", number: "", carrier: "", notes: "" });
    setContactModalOpen(false);
  };

  const handleEditContact = (contact) => {
    setSelectedContact(contact);
    setContactForm({
      name: contact.name || "",
      number: contact.number || "",
      carrier: contact.carrier || "",
      notes: contact.notes || ""
    });
    setContactModalOpen(true);
  };

  const handleDeleteContact = async () => {
    try {
      await api.delete(`/maturation-contact-pools/${deletingContact.id}`);
      toast.success("Contato excluído com sucesso!");
      dispatchContacts({ type: "DELETE_DATA", payload: deletingContact.id });
      setConfirmContactModalOpen(false);
      setDeletingContact(null);
    } catch (err) {
      toastError(err);
    }
  };

  // Renderizar aba de Maturações
  const renderMaturationsTab = () => (
    <>
      <Paper className={classes.statsCard}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              placeholder="Buscar maturação..."
              type="search"
              value={searchParam}
              onChange={handleSearch}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="INATIVA">Inativa</MenuItem>
                <MenuItem value="PROGRAMADA">Programada</MenuItem>
                <MenuItem value="EM_ANDAMENTO">Em Andamento</MenuItem>
                <MenuItem value="PAUSADA">Pausada</MenuItem>
                <MenuItem value="CANCELADA">Cancelada</MenuItem>
                <MenuItem value="FINALIZADA">Finalizada</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleOpenModal}
              startIcon={<AddIcon />}
            >
              Nova Maturação
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader}>Nome</TableCell>
              <TableCell className={classes.tableHeader} align="center">Status</TableCell>
              <TableCell className={classes.tableHeader} align="center">WhatsApp</TableCell>
              <TableCell className={classes.tableHeader} align="center">Início</TableCell>
              <TableCell className={classes.tableHeader} align="center">Término</TableCell>
              <TableCell className={classes.tableHeader} align="center">Enviadas</TableCell>
              <TableCell className={classes.tableHeader} align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {maturations.map((maturation) => (
              <TableRow key={maturation.id}>
                <TableCell>{maturation.name}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={formatStatus(maturation.status)}
                    color={getStatusColor(maturation.status)}
                    size="small"
                    className={classes.statusChip}
                  />
                </TableCell>
                <TableCell align="center">
                  {maturation.whatsapp?.name || "—"}
                </TableCell>
                <TableCell align="center">
                  {maturation.scheduledAt ? datetimeToClient(maturation.scheduledAt) : "—"}
                </TableCell>
                <TableCell align="center">
                  {maturation.endAt ? datetimeToClient(maturation.endAt) : "—"}
                </TableCell>
                <TableCell align="center">
                  {maturation.totalSent || 0}
                </TableCell>
                <TableCell align="center">
                  <div className={classes.actionButtons}>
                    {(maturation.status === "INATIVA" || maturation.status === "PROGRAMADA") && (
                      <Tooltip title="Iniciar">
                        <IconButton size="small" onClick={() => handleStart(maturation)}>
                          <PlayCircleOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {maturation.status === "EM_ANDAMENTO" && (
                      <Tooltip title="Pausar">
                        <IconButton size="small" onClick={() => handlePause(maturation)}>
                          <PauseCircleOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {maturation.status === "PAUSADA" && (
                      <Tooltip title="Retomar">
                        <IconButton size="small" onClick={() => handleResume(maturation)}>
                          <PlayCircleOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Relatório">
                      <IconButton
                        size="small"
                        onClick={() => history.push(`/maturations/${maturation.id}/report`)}
                      >
                        <AssessmentIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleEdit(maturation)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setConfirmModalOpen(true);
                          setDeletingMaturation(maturation);
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={7} />}
            {!loading && maturations.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className={classes.emptyState}>
                  <Typography variant="body1">
                    Nenhuma maturação encontrada. Crie sua primeira maturação!
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <Box display="flex" justifyContent="flex-end" p={2}>
          <TablePagination
            component="div"
            count={totalCount}
            page={pageNumber - 1}
            onPageChange={handlePageChange}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handlePageSizeChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Por página:"
          />
        </Box>
      </Paper>
    </>
  );

  // Renderizar aba de Contatos
  const renderContactsTab = () => (
    <>
      <Paper className={classes.statsCard}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={9}>
            <TextField
              fullWidth
              placeholder="Buscar contato..."
              type="search"
              value={searchParam}
              onChange={handleSearch}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleOpenContactModal}
              startIcon={<AddIcon />}
            >
              Novo Contato
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader}>Nome</TableCell>
              <TableCell className={classes.tableHeader} align="center">Número</TableCell>
              <TableCell className={classes.tableHeader} align="center">Operadora</TableCell>
              <TableCell className={classes.tableHeader}>Observações</TableCell>
              <TableCell className={classes.tableHeader} align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact) => (
              <TableRow key={contact.id}>
                <TableCell>{contact.name || "—"}</TableCell>
                <TableCell align="center">{contact.number}</TableCell>
                <TableCell align="center">
                  {contact.carrier && (
                    <Chip label={contact.carrier} size="small" className={classes.statusChip} />
                  )}
                </TableCell>
                <TableCell>{contact.notes || "—"}</TableCell>
                <TableCell align="center">
                  <div className={classes.actionButtons}>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleEditContact(contact)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setConfirmContactModalOpen(true);
                          setDeletingContact(contact);
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={5} />}
            {!loading && contacts.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className={classes.emptyState}>
                  <Typography variant="body1">
                    Nenhum contato encontrado. Adicione contatos para usar nas maturações!
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <Box display="flex" justifyContent="flex-end" p={2}>
          <TablePagination
            component="div"
            count={totalCount}
            page={pageNumber - 1}
            onPageChange={handlePageChange}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handlePageSizeChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Por página:"
          />
        </Box>
      </Paper>
    </>
  );

  return (
    <MainContainer>
      <MainHeader>
        <Title>Maturação</Title>
      </MainHeader>

      <Paper className={classes.tabsContainer}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <DescriptionIcon />
                <span>Maturações</span>
                {maturations.length > 0 && (
                  <Badge badgeContent={maturations.length} color="primary" />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <PeopleIcon />
                <span>Contatos</span>
                {contacts.length > 0 && (
                  <Badge badgeContent={contacts.length} color="primary" />
                )}
              </Box>
            }
          />
        </Tabs>
      </Paper>

      {activeTab === 0 && renderMaturationsTab()}
      {activeTab === 1 && renderContactsTab()}

      <MaturationModal
        open={maturationModalOpen}
        onClose={handleCloseModal}
        maturationId={selectedMaturation?.id}
        onSave={() => {
          handleCloseModal();
          fetchMaturations();
        }}
      />

      <ConfirmationModal
        title="Excluir Maturação"
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeletingMaturation(null);
        }}
        onConfirm={handleDelete}
      >
        <Typography>
          Tem certeza que deseja excluir a maturação "{deletingMaturation?.name}"? Esta ação não pode ser desfeita.
        </Typography>
      </ConfirmationModal>

      {/* Modal de Contato */}
      <Dialog
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedContact ? "Editar Contato" : "Novo Contato"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome (opcional)"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                variant="outlined"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Número *"
                value={contactForm.number}
                onChange={(e) => setContactForm({ ...contactForm, number: e.target.value })}
                variant="outlined"
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" margin="dense">
                <InputLabel>Operadora</InputLabel>
                <Select
                  value={contactForm.carrier}
                  onChange={(e) => setContactForm({ ...contactForm, carrier: e.target.value })}
                  label="Operadora"
                >
                  <MenuItem value="VIVO">Vivo</MenuItem>
                  <MenuItem value="TIM">Tim</MenuItem>
                  <MenuItem value="CLARO">Claro</MenuItem>
                  <MenuItem value="OI">Oi</MenuItem>
                  <MenuItem value="OUTRO">Outro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observações"
                value={contactForm.notes}
                onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })}
                variant="outlined"
                margin="dense"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContactModal} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={async () => {
              try {
                if (selectedContact?.id) {
                  await api.put(`/maturation-contact-pools/${selectedContact.id}`, contactForm);
                  toast.success("Contato atualizado!");
                } else {
                  await api.post("/maturation-contact-pools", contactForm);
                  toast.success("Contato adicionado!");
                }
                handleCloseContactModal();
                fetchContacts();
              } catch (err) {
                toastError(err);
              }
            }}
            color="primary"
            variant="contained"
            disabled={!contactForm.number}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Excluir Contato"
        open={confirmContactModalOpen}
        onClose={() => {
          setConfirmContactModalOpen(false);
          setDeletingContact(null);
        }}
        onConfirm={handleDeleteContact}
      >
        <Typography>
          Tem certeza que deseja excluir o contato "{deletingContact?.name || deletingContact?.number}"? Esta ação não pode ser desfeita.
        </Typography>
      </ConfirmationModal>
    </MainContainer>
  );
};

export default Maturations;

